import type { ReactNode } from 'react';
import { InfoPopover } from '../../components/InfoPopover';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';

const m = defineMessages({
  block: {
    id: 'common.ui.card.blockName',
    defaultMessage: 'блок',
    description: 'Запасное название блока для aria-label значка «i» у карточки, когда заголовок не строка',
  },
});

export interface CardProps {
  children: ReactNode;
  /** `lg` — крупная карточка/доска (padding 24, радиус 18). */
  size?: 'md' | 'lg';
  tone?: 'card' | 'soft' | 'plain';
  className?: string;
  title?: ReactNode;
  /** Правый верхний угол заголовка: счёт, срез, класс данных. */
  aside?: ReactNode;
  /**
   * Пояснение «как читать / как считается» — значком «i» у заголовка, а не
   * абзацем под блоком (07.10.2026): на виду остаётся только то, без чего
   * число читается неверно прямо сейчас; методика — по нажатию.
   */
  info?: ReactNode;
  id?: string;
}

export function Card({ children, size = 'md', tone = 'card', className, title, aside, info, id }: CardProps) {
  const t = useT();
  const classes = [
    'card',
    size === 'lg' ? 'card--lg' : '',
    tone === 'soft' ? 'card--soft' : '',
    tone === 'plain' ? 'card--plain' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={classes} id={id}>
      {(title || aside) && (
        <div className="card__head">
          {title ? (
            <h2 className={`card__title${info ? ' card__title--info' : ''}`}>
              {title}
              {info ? <InfoPopover icon="info" label={typeof title === 'string' ? title : t(m.block)}>{info}</InfoPopover> : null}
            </h2>
          ) : <span />}
          {aside ? <div className="card__aside">{aside}</div> : null}
        </div>
      )}
      {children}
    </section>
  );
}

export function Grid({
  cols = 4,
  children,
  className,
}: {
  cols?: 2 | 3 | 4 | 6 | 'main-rail' | '7-5';
  children: ReactNode;
  className?: string;
}) {
  return <div className={`grid grid--${cols} ${className ?? ''}`}>{children}</div>;
}

export function Stack({
  children,
  tight,
  className,
}: {
  children: ReactNode;
  tight?: boolean;
  className?: string;
}) {
  return <div className={`stack ${tight ? 'stack--tight' : ''} ${className ?? ''}`}>{children}</div>;
}
